import React, {useEffect, useState, Fragment} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import EnhancedTableHead from '../../components/Table/enhanced-table-head'
import TableToolbar from '../../components/Table/toolbar';
import {useTranslation} from 'react-i18next';
import LoadMore from '../../components/Table/load-more';
import {alert} from '../../redux/ducks/notification';
import {useDispatch} from 'react-redux';
import SearchBar from '../../components/Filters/search-bar';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import {Tooltip} from '@material-ui/core';
import {getCustomers} from '../../services/api/user-api-service';

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
  },
  paper: {
    marginBottom: theme.spacing(3),
    overflow: 'hidden',
  },
  tableContainer: {
    overflowX: 'auto',
  },
  table: {
    minWidth: 500,
  },
  link: {
    cursor: 'pointer'
  },
  addButton: {
    marginBottom: theme.spacing(2),
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
  }
}));

export default function Customers({history}) {
  const classes = useStyles();
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const [customers, setCustomers] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [next, setNext] = useState(undefined);
  const [selector, setSelector] = useState('');
  const [value, setValue] = useState('');

  const [order, setOrder] = useState({
    column: 'name',
    direction: 'asc'
  });

  const headCells = [
    {id: 'number', label: t('tables.customers.thead.number'), sortable: true},
    {id: 'name', label: t('tables.customers.thead.name'), sortable: true},
    {id: 'email', label: t('tables.customers.thead.email'), sortable: true},
    {id: 'phone', label: t('tables.customers.thead.phone'), sortable: false},
  ];

  const fields = [
    {value: 'number', name: t('tables.customers.thead.number')},
    {value: 'name', name: t('tables.customers.thead.name')},
    {value: 'email', name: t('tables.customers.thead.email')},
    {value: 'phone', name: t('tables.customers.thead.phone')},
  ];

  const handleRequestSort = (event, property) => {
    const isAsc = order.column === property && order.direction === 'asc';
    setOrder(prevState => {
      return {
        ...prevState,
        column: property,
        direction: isAsc ? 'desc' : 'asc'
      }
    })
  };

  const fetchCustomers = async (filters = null, loadMore = false) => {
    if (null === filters) {
      filters = buildFilters();
    }

    try {
      const response = await getCustomers(filters);
      const data = response.data;
      setCustomers(loadMore ? customers.concat(data['items']) : data['items']);
      setTotalItems(data['meta'].total);
      setNext(true === data['meta'].hasNext ? `${filters.replace(/&page=\d+$/, '')}&page=${data['meta'].page + 1}` : undefined);
    } catch (e) {
      dispatch(alert(t('errors.common.server')));
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [order]); // eslint-disable-line

  const handleClick = id => {
    history.push(`/dashboard/customers/${id}`);
  };

  const handleSelector = event => {
    setSelector(event.target.value);
  };

  const handleValue = event => {
    setValue(event.target.value);
  };

  const buildFilters = () => {
    let filters = `&sort=${order.column}&order=${order.direction}`;

    switch (selector) {
      case 'number':
        filters += `&number=${value}`;
        break;
      case 'name':
        filters += `&name=${value}`;
        break;
      case 'email':
        filters += `&email=${value}`;
        break;
      case 'phone':
        filters += `&phone=${value}`;
        break;
      default:
        filters += ''
    }

    return filters;
  };

  const handleFiltersSubmit = event => {
    event.preventDefault();
    let filters = buildFilters();
    fetchCustomers(filters);
  };

  const content = (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <TableToolbar name={`${t('tables.customers.name')} (${totalItems})`}/>
        <TableContainer className={classes.tableContainer}>
          <Table className={classes.table} size="small">
            <EnhancedTableHead
              order={order.direction}
              orderBy={order.column}
              onRequestSort={handleRequestSort}
              rowCount={customers.length}
              headCells={headCells}
            />
            <TableBody>
              {customers.map((row) => (
                <TableRow className={classes.link} hover key={row.id} onClick={() => handleClick(row.id)}>
                  <TableCell>{row.number}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>{row.phone}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {next &&
          <LoadMore buttonText={t('tables.common.load_more')} loadMore={() => fetchCustomers(next, true)}/>
        }
      </Paper>
    </div>
  );

  return (
    <Fragment>
      <Box className={classes.addButton}>
        <Tooltip title={t('tooltip.customer.create')} placement={'right'} arrow>
          <AddCircleIcon
            onClick={() => history.push('/dashboard/customers/create')}
            fontSize={'large'}
            color={'primary'}
          />
        </Tooltip>
      </Box>
      <SearchBar
        selector={selector}
        value={value}
        handleSelector={handleSelector}
        handleSubmit={handleFiltersSubmit}
        fields={fields}
        handleValue={handleValue}
      />
      {totalItems > 0 ? content : (
        <Paper className={classes.emptyState}>
          <Typography variant="body1" color="textSecondary">
            {t('tables.common.no_data')}
          </Typography>
        </Paper>
      )}
    </Fragment>
  )
};
